import { cn } from "../foundations/cn.js";
import { HitChip, type HitChipProps } from "./HitChip.js";

export interface RecentHitsProps {
  hits: readonly (Omit<HitChipProps, "className" | "surface"> & { id: string })[];
  /** The strip's name, shown at its start and read as the region's name: "Recent hits". */
  label: string;
  /** A short live marker before the label: "Live". Omit when the hits aren't live. */
  liveLabel?: string;
  className?: string;
}

/**
 * A glass bar of recent hits, newest first. On a wide screen with motion allowed it drifts slowly
 * for a few loops, then rests, pausing under the pointer or focus; on phones and under reduced
 * motion it scrolls by hand.
 */
export function RecentHits({ hits, label, liveLabel, className }: RecentHitsProps) {
  const chips = hits.map(({ id, ...hit }) => <HitChip key={id} surface="plain" {...hit} />);
  return (
    <section
      aria-label={label}
      className={cn(
        "glass flex items-center gap-4 rounded-pill p-2 shadow-float sm:pl-5",
        className,
      )}
    >
      <p className="flex shrink-0 items-center gap-2 font-body text-body-b3-strong text-text-secondary max-sm:sr-only">
        {liveLabel && (
          <span className="flex items-center gap-1.5 font-mono text-mono-meta text-status-danger uppercase">
            <span
              aria-hidden
              className="size-1.5 rounded-pill bg-status-danger motion-safe:animate-pulse"
            />
            {liveLabel}
          </span>
        )}
        {label}
      </p>
      {/* A tab stop, so a keyboard can scroll it on phones; focus also pauses the drift. */}
      <div
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be reachable (WCAG 2.1.1).
        tabIndex={0}
        className="group/hits relative min-w-0 flex-1 rounded-pill outline-hidden focus-visible:focus-ring overflow-x-auto [scrollbar-width:none] md:overflow-hidden md:[mask-image:linear-gradient(90deg,transparent,black_4%,black_96%,transparent)]"
      >
        <div className="flex w-max gap-3 motion-safe:md:animate-marquee motion-safe:md:group-hover/hits:[animation-play-state:paused] motion-safe:md:group-focus-within/hits:[animation-play-state:paused]">
          <ul className="flex gap-3">
            {chips.map((chip) => (
              <li key={chip.key}>{chip}</li>
            ))}
          </ul>
          {/* The copy that makes the loop seamless: inert (no tab stops, unseen by assistive tech),
              and only drawn where it moves. */}
          <ul aria-hidden inert className="hidden gap-3 motion-safe:md:flex">
            {chips.map((chip) => (
              <li key={`copy-${chip.key}`}>{chip}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
